import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ALIGNMENT_AXES,
  BREATH_PHASES,
  breathCycleSeconds,
  SUCCESS_SCORE_MAX,
  type AlignmentSnapshot,
  type BreathworkPattern,
  type SuccessScoreComponent,
} from '@mlm/shared/coaching';
import type { SuccessScore } from '@mlm/shared';
import { cn } from '@mlm/ui/lib/cn';

/**
 * The "Daylight cards" drawing set (2026-10 redesign): the brand mark, the
 * rings that turn the day's numbers into pictures, and the rail that every
 * collection on Home scrolls in. All imagery is SVG or CSS; there are no photos.
 */

const STAR = 'M32 16 L35.2 28.8 L48 32 L35.2 35.2 L32 48 L28.8 35.2 L16 32 L28.8 28.8 Z';
const STAR_WIDE = 'M32 8 L37 27 L56 32 L37 37 L32 56 L27 37 L8 32 L27 27 Z';

/** The ink disc, gold ring and four-point star. On a dark ground the disc goes and the gold lightens. */
export function BrandMark({ size = 28, onDark }: { size?: number; onDark?: boolean }) {
  const gold = onDark ? '#E6C87A' : '#B08D3F';
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden className="shrink-0">
      {onDark ? (
        <circle cx="32" cy="32" r="31" fill="#F6F4EE" fillOpacity="0.08" />
      ) : (
        <circle cx="32" cy="32" r="31" fill="#10201A" />
      )}
      <circle cx="32" cy="32" r="22" fill="none" stroke={gold} strokeWidth="2.5" />
      <path d={STAR} fill={gold} />
    </svg>
  );
}

/** The bare four-point star: an eyebrow mark, a toolkit tile's corner. */
export function StarGlyph({ size = 14, color = '#B08D3F' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden className="shrink-0">
      <path d={STAR_WIDE} fill={color} />
    </svg>
  );
}

/** Done: a verde check on a leaf-wash disc. */
export function DoneMark({ size = 22 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Done"
      className="shrink-0"
    >
      <circle cx="12" cy="12" r="10.5" fill="#DFEFD2" stroke="#277A0B" strokeWidth="1" />
      <path
        d="M8 12.3l2.6 2.6L16 9.4"
        stroke="#277A0B"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Not done yet. `milestone` draws the dashed gold ring a goal's next step wears. */
export function PendingMark({ size = 22, milestone }: { size?: number; milestone?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Pending"
      className="shrink-0"
    >
      <circle
        cx="12"
        cy="12"
        r="10.5"
        stroke={milestone ? '#B08D3F' : 'rgba(16,32,26,0.3)'}
        strokeWidth="1"
        strokeDasharray={milestone ? '2 2.5' : undefined}
      />
    </svg>
  );
}

/** The four axes on a dark ground, in `ALIGNMENT_AXES` order. */
const AXIS_COLORS_DARK = ['#8FD27A', '#E6C87A', '#B7D9A8', '#CFD4CC'] as const;

/** One axis's dot colour on the reading card, so the list beside the ring matches its arc. */
export function AxisDot({ index }: { index: number }) {
  return (
    <span
      aria-hidden
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ background: AXIS_COLORS_DARK[index % AXIS_COLORS_DARK.length] }}
    />
  );
}

/**
 * Today's alignment as four quarter-arcs, one per axis, each filled to its
 * score, with the overall number and its band in the middle. The numbers are
 * reflective indices; the ring says so through the caption beside it.
 */
export function AxesRing({
  alignment,
  size,
  caption,
  numberClass,
}: {
  alignment: AlignmentSnapshot;
  size: number;
  caption: string;
  numberClass: string;
}) {
  const r = 58;
  const c = 2 * Math.PI * r;
  const quarter = c / 4;
  const gap = 7;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 150 150" width={size} height={size} aria-hidden>
        {ALIGNMENT_AXES.map(({ key }, i) => {
          const offset = -(i * quarter + gap / 2);
          const fill = (Math.max(0, Math.min(100, alignment[key])) / 100) * (quarter - gap);
          return (
            <g key={key} transform="rotate(-90 75 75)">
              <circle
                cx="75"
                cy="75"
                r={r}
                fill="none"
                stroke="rgba(246,244,238,0.1)"
                strokeWidth="10"
                strokeDasharray={`${quarter - gap} ${c}`}
                strokeDashoffset={offset}
              />
              <circle
                cx="75"
                cy="75"
                r={r}
                fill="none"
                stroke={AXIS_COLORS_DARK[i]}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={`${fill} ${c}`}
                strokeDashoffset={offset}
              />
            </g>
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('font-display font-medium leading-none', numberClass)}>
          {alignment.overall}
        </span>
        <span className="mt-1 font-mono text-[0.5rem] uppercase tracking-[0.12em] text-paper/60">
          {caption}
        </span>
      </div>
    </div>
  );
}

const SCORE_ORDER = Object.keys(SUCCESS_SCORE_MAX) as SuccessScoreComponent[];

/**
 * The Success Score as six segments, each as long as its part's share of 100
 * and filled by what the member has earned in it. `highlight` warms the empty
 * track of the part the tip under the ring points at.
 */
export function ScoreRing({
  score,
  size,
  highlight,
  numberClass,
}: {
  score: SuccessScore;
  size: number;
  highlight: SuccessScoreComponent | null;
  numberClass: string;
}) {
  const r = 58;
  const c = 2 * Math.PI * r;
  const gap = 5;
  let cum = 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 140 140" width={size} height={size} aria-hidden>
        {SCORE_ORDER.map((part) => {
          const max = SUCCESS_SCORE_MAX[part];
          const value = Math.max(0, Math.min(max, score.breakdown[part]));
          const len = (max / 100) * c;
          const offset = -(cum + gap / 2);
          cum += len;
          return (
            <g key={part} transform="rotate(-90 70 70)">
              <circle
                cx="70"
                cy="70"
                r={r}
                fill="none"
                stroke={part === highlight ? 'rgba(176,141,63,0.4)' : 'rgba(16,32,26,0.08)'}
                strokeWidth="11"
                strokeDasharray={`${len - gap} ${c}`}
                strokeDashoffset={offset}
              />
              <circle
                cx="70"
                cy="70"
                r={r}
                fill="none"
                stroke="#277A0B"
                strokeWidth="11"
                strokeDasharray={`${Math.max(0, (value / 100) * c - gap)} ${c}`}
                strokeDashoffset={offset}
              />
            </g>
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('font-display font-medium leading-none text-ink', numberClass)}>
          {score.total}
        </span>
        <span className="mt-1 max-w-[80%] text-center font-mono text-[0.5rem] uppercase tracking-[0.08em] text-sage">
          {score.label}
        </span>
      </div>
    </div>
  );
}

/** Breathe in, hold, breathe out, hold: verde, gold, sage-green, light gold. */
const PHASE_COLORS = ['#277A0B', '#B08D3F', '#5F8F4A', '#D6C38F'] as const;

/** A pattern drawn as its breath: one arc per phase, as long as the phase lasts. */
export function BreathPatternRing({ pattern, size }: { pattern: BreathworkPattern; size: number }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const total = breathCycleSeconds(pattern);
  let cum = 0;
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden className="block">
      <circle cx="32" cy="32" r={r} fill="none" stroke="rgba(16,32,26,0.08)" strokeWidth="5" />
      {BREATH_PHASES.map((phase, i) => {
        const seconds = pattern[phase];
        if (seconds <= 0 || total <= 0) return null;
        const len = (seconds / total) * c;
        const offset = -(cum + 3);
        cum += len;
        return (
          <circle
            key={phase}
            cx="32"
            cy="32"
            r={r}
            fill="none"
            stroke={PHASE_COLORS[i]}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${Math.max(0, len - 6)} ${c}`}
            strokeDashoffset={offset}
            transform="rotate(-90 32 32)"
          />
        );
      })}
      <circle cx="32" cy="32" r="11" fill="#DFEFD2" />
    </svg>
  );
}

/** A section heading with its "see all" link: Fraunces left, verde mono caps right. */
export function SectionHead({
  title,
  link,
  size = 'phone',
  className,
}: {
  title: ReactNode;
  link?: { label: string; to: string } | null;
  size?: 'phone' | 'desktop' | 'column';
  className?: string;
}) {
  return (
    <div className={cn('flex items-baseline justify-between gap-4 pb-2.5', className)}>
      <h2
        className={cn(
          'font-display font-medium text-ink',
          size === 'phone' && 'text-[21px]',
          size === 'desktop' && 'text-2xl',
          size === 'column' && 'text-[22px]',
        )}
      >
        {title}
      </h2>
      {link && (
        <Link
          to={link.to}
          className="shrink-0 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-verde hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verde"
        >
          {link.label}
        </Link>
      )}
    </div>
  );
}

/**
 * A horizontal rail of tiles. On a phone it bleeds to the screen edge with the
 * page gutter as padding, so a tile peeking in from the right says "scroll".
 */
export function Rail({
  children,
  gap = 'md',
  bleed = true,
  className,
}: {
  children: ReactNode;
  gap?: 'sm' | 'md' | 'lg';
  bleed?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rail pb-2.5 pt-0.5',
        gap === 'sm' && 'gap-2.5',
        gap === 'md' && 'gap-3',
        gap === 'lg' && 'gap-3.5',
        bleed ? 'scroll-px-5 px-5' : 'px-0.5',
        className,
      )}
    >
      {children}
    </div>
  );
}
